import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  FormControl,
  Heading,
  Icon,
  IconSize,
  Select,
  SideSheet,
  SideSheetSize,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  ToastAction,
  ToastType,
  theme,
  useModalState,
  useToastsDispatch,
} from '@bechtel/gds';
import { useDebounce } from '@bechtel/ht-clients-common';
import {
  FilterConditionInput,
  FilterInput,
  OperatorType,
  OutboundInterface,
  SearchInput,
  SortType,
} from '@bechtel/ht-types';
import { get } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary, ErrorRetry } from '~/components';
import { OutboundSubscriptionManager } from '~/components/atoms/subscription-manager';
import { TextWithEllipsis } from '~/components/atoms/text-with-ellipsis';
import { GET_OUTBOUND_INTERFACE_COUNT } from '~/gql';
import { useResyncFailureData } from '~/hooks/use-resync-failure-data';
import { useSearchOutboundInterface } from '~/hooks/use-search-outbound-interface';
import { EmptySearchResultIcon } from '~/images';
import { Errors, Status, errorList, statusList } from './form-data';
import { getFontColor, useTableHeaders } from './header';
import {
  ErrorDetailsHeaderStyled,
  FooterStyled,
  IconBoxStyled,
  IconStyled,
  ItemsContainerStyled,
  JsonStyledBox,
  PreStyle,
  SearchFieldComponentStyled,
  SubContainerStyled,
  SyncContainerStyled,
  TableCellStyled,
  TableColumnStyled,
  TableHeaderStickyStyled,
  TableStyled,
} from './styled';
import { SyncRowProps } from './types';

const LIMIT = 20;
const DropdownItem = ({ item }: { item: any }) => {
  return (
    <Text size="400" color="grey.1000" style={{ zIndex: 3 }}>
      {item.label}
    </Text>
  );
};

const SyncRow: FC<SyncRowProps> = ({ rowData, onSync, openErrorDetails, selectedJobQueueId }) => {
  const dispatchToast = useToastsDispatch();
  const indentifierDetails =
    rowData.toolOrder?.orderNumber || rowData.toolCheckIn?.checkInNumber || rowData.toolTransfer?.toolTransferNumber;
  const tableHeaders = useTableHeaders();
  const copyOutboundInterfaceId = (id: string) => {
    navigator.clipboard.writeText(id);
    const toastAction: ToastAction = {
      type: 'ADD_TOAST',
      payload: {
        toastType: ToastType.SUCCESS,
        content: `${id} Copied successfuly!`,
        autoDismissTimeout: 3000,
      },
    };
    dispatchToast(toastAction);
  };
  return (
    <tr>
      {tableHeaders.map((header, index) => {
        return (
          <TableColumnStyled key={index}>
            <TableCellStyled justifyContent={{ sm: 'center' }} alignItems={{ sm: 'center' }}>
              {header.render ? (
                header.render({
                  ...rowData,
                  loading: selectedJobQueueId == rowData.id,
                  onSync: () => onSync(rowData),
                  status: rowData.status,
                })
              ) : (
                <TextWithEllipsis
                  style={{
                    cursor:
                      header.key == 'response' ||
                      header.key == 'message' ||
                      header.key == 'id' ||
                      header.key == 'identifier.0'
                        ? 'pointer'
                        : '',
                    width: '100%',
                  }}
                  onClick={() => {
                    header.key == 'response'
                      ? openErrorDetails(rowData.response, 'Response')
                      : header.key == 'message'
                      ? openErrorDetails(rowData.message, 'Payload')
                      : header.key == 'id'
                      ? copyOutboundInterfaceId(rowData.id)
                      : header.key == 'identifier.0'
                      ? copyOutboundInterfaceId(indentifierDetails)
                      : () => {};
                  }}
                  size="300"
                  color={header.key == 'status' ? getFontColor(rowData.status || '') : 'black'}
                  title={header.label == 'Identifier' ? indentifierDetails : get(rowData, header.key, '')}
                >
                  {header.label == 'Identifier' ? indentifierDetails : get(rowData, header.key, '')}
                </TextWithEllipsis>
              )}
              {header.key === 'id' || header.key == 'identifier.0' ? (
                <IconBoxStyled>
                  <IconStyled
                    icon="copy"
                    size={IconSize.LARGE}
                    iconStyle="regular"
                    className="icon"
                    onClick={() =>
                      header.key == 'identifier.0'
                        ? copyOutboundInterfaceId(indentifierDetails)
                        : copyOutboundInterfaceId(rowData.id)
                    }
                  />
                </IconBoxStyled>
              ) : null}
            </TableCellStyled>
          </TableColumnStyled>
        );
      })}
    </tr>
  );
};

export const Sync = () => {
  const tableHeaders = useTableHeaders();
  const [prevScrollTop, setPrevScrollTop] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceSearchQuery = useDebounce(searchQuery, 500);
  const [selectedErrorType, setSelectedErrorType] = useState<string>(Errors.ALL);
  const [selectedStatus, setSelectedStatus] = useState<string>(Status.ALL);
  const { visible: visibleErrorDetails, open: openErrorDetails, close: closeErrorDetails } = useModalState(false);
  const [selectedErrorDetails, setErrorDetails] = useState('');
  const [selectedJobQueueId, setJobQueueId] = useState('');
  const [detailsHeaderTitle, setDetailsHeaderTitle] = useState('');
  const { searchOutboundInterfaces, data, loading, error, refetch, fetchMore } = useSearchOutboundInterface();
  const dispatchToast = useToastsDispatch();
  const { retryFailedOutboundInterface, loading: updatingRolePermissions } = useResyncFailureData();
  const retryOutboundInterface = (outboundInterface: OutboundInterface) => {
    setJobQueueId(outboundInterface.id);
    retryFailedOutboundInterface(
      {
        id: [outboundInterface.id],
      },
      (data) => {
        setJobQueueId('');
        const rolePermissionToast: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: data.errors ? ToastType.DANGER : ToastType.SUCCESS,
            content: data.errors
              ? `Sync Job has been failed.Please try again!`
              : `Sync Job has been started successfuly!`,
            autoDismissTimeout: 3000,
          },
        };
        dispatchToast(rolePermissionToast);
      }
    );
  };

  const filterValues = useMemo(() => {
    const or: FilterConditionInput[] = [];
    const and: FilterConditionInput[] = [];

    if (selectedErrorType != Errors.ALL) {
      and.push({ field: 'errorType', operator: OperatorType.EQUAL, value: selectedErrorType });
    }
    if (selectedStatus != Status.ALL) {
      and.push({ field: 'status', operator: OperatorType.EQUAL, value: selectedStatus });
    }
    const filterInputObj: FilterInput = {
      or: or,
      and: and,
    };
    return { ...filterInputObj };
  }, [selectedErrorType, selectedStatus]);
  const { data: totalData } = useQuery<{ outboundInterfaceCount: number }, { searchInput: SearchInput }>(
    GET_OUTBOUND_INTERFACE_COUNT,
    {
      variables: {
        searchInput: {
          filterInput: filterValues,
          sortInput: [],
          searchText: '',
        },
      },
    }
  );
  const totalRecords = totalData?.outboundInterfaceCount;
  const onErrorDetails = (error: string, type: string) => {
    setErrorDetails(JSON.parse(error || ''));
    setDetailsHeaderTitle(type);
    openErrorDetails();
  };

  useEffect(() => {
    searchOutboundInterfaces({
      filterInput: filterValues,
      paginationInput: {
        limit: LIMIT,
        offset: 0,
      },
      sortInput: [{ field: 'updatedDate', sort: SortType.DESC }],
      searchText: debounceSearchQuery,
    });
  }, [debounceSearchQuery, searchOutboundInterfaces, filterValues]);

  const isExpendableColumn = (id) => {
    let isEnabled = false;
    if (id == 'identifier.0' || id == 'outboundAction' || id == 'id') {
      isEnabled = true;
    }
    return isEnabled;
  };
  const outboundInterfaces: OutboundInterface[] = get(data, 'searchOutboundInterfaces.items', []);
  const handleScroll = (e) => {
    if (!totalRecords) return;

    const { scrollTop, clientHeight, scrollHeight } = e.target;
    setPrevScrollTop(scrollTop);
    const isVerticalScroll = scrollTop !== prevScrollTop;
    // Only fetch more if we have the potential for more data
    if (outboundInterfaces.length % LIMIT === 0) {
      if (
        scrollTop + clientHeight + 1 >= scrollHeight &&
        !loading &&
        outboundInterfaces &&
        outboundInterfaces.length < totalRecords &&
        isVerticalScroll
      ) {
        fetchMore({
          variables: {
            searchInput: {
              filterInput: filterValues,
              paginationInput: {
                limit: LIMIT,
                offset: outboundInterfaces.length,
              },
              sortInput: [{ field: 'updatedDate', sort: SortType.DESC }],
              searchText: debounceSearchQuery,
            },
          },
        });
      }
    }
  };

  return (
    <>
      <OutboundSubscriptionManager />
      <SyncContainerStyled>
        <SubContainerStyled>
          <Box flexDirection={{ sm: 'column' }}>
            <Box
              py={{ sm: '550' }}
              px={{ sm: '450' }}
              gap={{ sm: '700' }}
              css="width: 100%"
              justifyContent={{ sm: 'space-between' }}
            >
              <Box flexDirection={{ sm: 'column' }} gap={{ sm: '300' }}>
                <Text weight={'600'}>Sync Data</Text>
                <Text size={'300'} color="red.700">
                  This may take several hours to complete
                </Text>
              </Box>
              <Box gap={{ sm: '400' }} style={{ width: '60%' }}>
                <Box style={{ width: '400px' }}>
                  <SearchFieldComponentStyled
                    size={TextFieldSize.LARGE}
                    iconSize={IconSize.MEDIUM}
                    placeholder={`Search`}
                    type="search"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchQuery(e.target.value);
                    }}
                    value={searchQuery}
                  />
                </Box>

                <FormControl
                  style={{ width: '200px' }}
                  label={
                    <Text family="graphik" size="300" color="bechtel.blue.800">
                      Error Type
                    </Text>
                  }
                >
                  <Select
                    data-testid="error-type"
                    size={TextFieldSize.MEDIUM}
                    appearance={TextFieldAppearance.NORMAL}
                    items={errorList}
                    itemComponent={DropdownItem}
                    selectedComponent={DropdownItem}
                    initialSelectedItem={errorList[0]}
                    popperStyle={{ zIndex: 2 }}
                    onSelectedItemChange={(e) => {
                      setSelectedErrorType(e.selectedItem.value);
                    }}
                  />
                </FormControl>

                <FormControl
                  style={{ width: '200px' }}
                  label={
                    <Text family="graphik" size="300" color="bechtel.blue.800">
                      Status
                    </Text>
                  }
                >
                  <Select
                    data-testid="status"
                    size={TextFieldSize.MEDIUM}
                    appearance={TextFieldAppearance.NORMAL}
                    items={statusList}
                    itemComponent={DropdownItem}
                    selectedComponent={DropdownItem}
                    initialSelectedItem={statusList[0]}
                    popperStyle={{ zIndex: 2 }}
                    onSelectedItemChange={(e) => {
                      setSelectedStatus(e.selectedItem.value);
                    }}
                  />
                </FormControl>
              </Box>
            </Box>
          </Box>

          <ItemsContainerStyled onScroll={handleScroll}>
            {outboundInterfaces && outboundInterfaces?.length > 0 && (
              <TableStyled>
                <thead data-testid="header">
                  <tr>
                    {tableHeaders.map((header) => (
                      <TableHeaderStickyStyled
                        widthExpanded={isExpendableColumn(header.key)}
                        data-testid={`header-${header.id}`}
                        key={header.id}
                      >
                        <Box flexShrink={{ sm: '0' }} gap={{ sm: '100' }} alignItems={{ sm: 'center' }}>
                          <Text size="300" color={'grey.1000'}>
                            {header.label}
                          </Text>
                        </Box>
                      </TableHeaderStickyStyled>
                    ))}
                  </tr>
                </thead>

                <tbody data-testid="entity-list-container">
                  {outboundInterfaces?.map((rowData) => (
                    <SyncRow
                      data-testid={`entity-${rowData.id}`}
                      key={rowData.id}
                      rowData={rowData}
                      onCompleted={() => {}}
                      onError={() => {}}
                      onSync={retryOutboundInterface}
                      openErrorDetails={onErrorDetails}
                      selectedJobQueueId={selectedJobQueueId}
                    />
                  ))}
                </tbody>
              </TableStyled>
            )}
            {!loading && error && (
              <Box
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                flexGrow={{ sm: 1 }}
                flexDirection={{ sm: 'column' }}
                p={{ sm: '500' }}
                data-testid="user-list-retry"
              >
                <ErrorRetry refetch={refetch}>
                  There was a problem loading the details. Please click the button below to try again.
                </ErrorRetry>
              </Box>
            )}
            {!loading && !error && !outboundInterfaces?.length && (
              <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} pt={{ sm: '900' }} css="width:100%">
                <EmptySearchResultIcon></EmptySearchResultIcon>
                <Heading size="400" weight="600" color="grey.600">
                  No Data found.
                </Heading>
                <Box mt={{ sm: '400' }}>
                  <Text size="400" color="grey.600" style={{ textAlign: 'center', lineHeight: '1.57' }}>
                    Try changing your search or filters to view events.
                  </Text>
                </Box>
              </Box>
            )}
            {loading && (
              <Box
                data-testid="entity-list-loader"
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                flexGrow={{ sm: 1 }}
                p={{ sm: '500' }}
              >
                <Spinner size={SpinnerSize.LARGE} />
              </Box>
            )}
          </ItemsContainerStyled>
          {!loading && outboundInterfaces && outboundInterfaces?.length > 0 && (
            <FooterStyled>
              {/* <FooterButtonStyled onClick={() => {}}>
                {syncMutationLoading ? (
                  <Spinner />
                ) : (
                  <Box gap={{ sm: '400' }}>
                    <Icon size={IconSize.MEDIUM} icon="rotate" fillColor="white" iconStyle="solid" />
                    <Text weight="600" size="400" family="graphikCompact">
                      Sync all above
                    </Text>
                  </Box>
                )}
              </FooterButtonStyled> */}
            </FooterStyled>
          )}
        </SubContainerStyled>

        {/* {syncError && <ErrorModal title={'Sync Error'} error={getError(syncError)} close={() => setSyncError(null)} />} */}
      </SyncContainerStyled>
      <ErrorBoundary>
        <SideSheet visible={visibleErrorDetails} onDismiss={closeErrorDetails} size={SideSheetSize.DEFAULT}>
          <Box flexDirection={{ sm: 'column' }}>
            <ErrorDetailsHeaderStyled>
              <Text color="white" size="550" weight="600">
                {detailsHeaderTitle} Details
              </Text>
              <Box>
                <Button
                  style={{ backgroundColor: `${theme.colors.primary[700]}` }}
                  size={ButtonSize.SMALL}
                  onClick={closeErrorDetails}
                  appearance={ButtonAppearance.TERTIARY}
                >
                  <Icon fillColor="white" icon="close" size={IconSize.LARGE} />
                </Button>
              </Box>
            </ErrorDetailsHeaderStyled>
            <JsonStyledBox>
              <pre style={PreStyle}>{JSON.stringify(selectedErrorDetails, null, 2)}</pre>
            </JsonStyledBox>
          </Box>
        </SideSheet>
      </ErrorBoundary>
    </>
  );
};
